import * as signalR from '@microsoft/signalr';
import { firstValueFrom } from 'rxjs';
import {
  Auth0AuthenticationService,
  AuthenticationService,
  SecurityRealtimeConnector,
} from '@ups/security';
import {
  HubKeepAlive,
  hubLogger,
  keepHubAlive,
  neverGiveUpRetryPolicy,
} from './signalr-resilience';

/**
 * #34116: NotificationHub methods this permission connection receives but has no use for. The hub puts every
 * connection in the user's notification group and sends `onNotificationsReceived` on connect (plus dismiss /
 * snooze fan-out); NotificationsService handles those on its own connection. Registering a no-op here keeps
 * the SignalR client from logging "No client method with the name ... found" (My UPS, Resource Allocation).
 */
export const IGNORED_NOTIFICATION_HUB_METHODS: readonly string[] = [
  'onNotificationsReceived',
  'onNotificationDismissed',
  'onNotificationSnoozed',
];

/**
 * #34116: web SignalR transport for Software Security permission-change pushes. Connects to the SS
 * notification hub (environment.sockets.signalR.url) with the Auth0 access token and reconnects on drop and keeps
 * retrying forever (neverGiveUpRetryPolicy, restart on close/visible/online; #36932). `start()` never throws: a connection failure leaves sign-in
 * and the initial permission load untouched, so permissions stay cache-first exactly as before.
 *
 * Constructed only by createWebSecurityRealtimeConnector, and only when a hub URL is configured, so it never
 * calls withUrl on an empty URL (which would throw during app init).
 */
export class WebSecurityRealtimeConnector implements SecurityRealtimeConnector {
  private readonly connection: signalR.HubConnection;
  private readonly keepAlive: HubKeepAlive;

  constructor(authService: AuthenticationService, hubUrl: string) {
    this.connection = new signalR.HubConnectionBuilder()
      .withUrl(hubUrl, {
        withCredentials: false,
        accessTokenFactory: async () => {
          try {
            const token = await firstValueFrom(
              authService.getAccessTokenSilently()
            );
            return token || '';
          } catch {
            return '';
          }
        },
      })
      .withAutomaticReconnect(neverGiveUpRetryPolicy)
      .configureLogging(hubLogger('WebSecurityRealtimeConnector'))
      .build();
    this.keepAlive = keepHubAlive(
      this.connection,
      'WebSecurityRealtimeConnector'
    );
    for (const method of IGNORED_NOTIFICATION_HUB_METHODS) {
      this.connection.on(method, () => undefined);
    }
  }

  on(eventName: string, handler: () => void): void {
    this.connection.on(eventName, handler);
  }

  async start(): Promise<void> {
    this.keepAlive.enable();
    try {
      if (this.connection.state === signalR.HubConnectionState.Disconnected) {
        await this.connection.start();
      }
    } catch (error) {
      // Swallow: a realtime transport failure must never break sign-in or the initial permission load.
      console.warn(
        'WebSecurityRealtimeConnector: hub start failed (non-fatal)',
        error
      );
      this.keepAlive.retryLater();
    }
  }

  async stop(): Promise<void> {
    this.keepAlive.disable();
    try {
      await this.connection.stop();
    } catch {
      // ignore
    }
  }
}

/**
 * #34116: builds the web permission-change transport for the SECURITY_REALTIME_CONNECTOR provider. Every web
 * app that has the SS notification hub URL configured AND signs in with Auth0 gets it, independent of the legacy
 * featuresSwitch.enableSignalR notification switch, so a signed-in user in any Auth0 app re-reads permissions on
 * an admin change. Returns null (SecurityService's optional inject is then a no-op and permissions stay
 * cache-first) when no hub URL is set, or when the app's AuthenticationService is not Auth0: the server keys the
 * push group by auth0 id and resolves the hub user by Users.Auth0UserId, so an Entra (MSAL) or InternalAuth
 * session could never be reached. Dual-login apps resolve their AuthenticationService per boot, so this follows
 * the issuer the session actually rides; SecurityService additionally connects only for an 'auth0' identity.
 */
export function createWebSecurityRealtimeConnector(
  authService: AuthenticationService,
  hubUrl: string | null | undefined,
  host: PermissionPushHost = { isDevOrPrHost: false, optedIn: false }
): WebSecurityRealtimeConnector | null {
  if (!hubUrl || !(authService instanceof Auth0AuthenticationService)) {
    return null;
  }
  if (host.isDevOrPrHost && !host.optedIn) {
    return null;
  }
  return new WebSecurityRealtimeConnector(authService, hubUrl);
}

/**
 * #34116 review F7: local dev and PR slots build against the STAGING hub URL, so without this every
 * `nx serve` and PR deployment would hold a live connection to the staging hub and react to staging admin
 * edits. Those hosts get no permission-push connection unless a developer opts in for one browser with
 * `localStorage.setItem('ups.permissionPush.devOptIn', '1')`. Stg and prod hosts are unaffected.
 */
export interface PermissionPushHost {
  /** True on localhost or a PR-numbered app service (`isOverrideAllowed()` from @ups/xplat/core). */
  isDevOrPrHost: boolean;
  /** True when this browser opted in via {@link PERMISSION_PUSH_DEV_OPT_IN_KEY}. */
  optedIn: boolean;
}

export const PERMISSION_PUSH_DEV_OPT_IN_KEY = 'ups.permissionPush.devOptIn';

/** Reads the dev/PR opt-in. Storage can throw (private mode, blocked site data): treat that as not opted in. */
export function readPermissionPushDevOptIn(): boolean {
  try {
    return (
      globalThis.localStorage?.getItem(PERMISSION_PUSH_DEV_OPT_IN_KEY) === '1'
    );
  } catch {
    return false;
  }
}
